import {
  ACCENTS,
  ALL_NODES,
  BAG_MAX,
  BOXES,
  CLAPPER,
  CLOTHS,
  COACH,
  COOLER,
  ENEMY_KINDS,
  ENEMY_SPAWNS,
  HEAD_LABELS,
  HISS,
  KEEL,
  MAP_H,
  MAP_W,
  MARKS,
  MEND_R,
  NPCS,
  ORIGINS,
  PLAYER_RADIUS,
  RECIPES,
  SHOP_R,
  STAIRS,
  STAIR_STEP,
  STORY,
  STORY_SPOTS,
  storyOpen,
  EDGE,
  LAST_STOP,
  LEAK_EDGE,
  MARGIN_STEP,
  BRANCH_START,
  T_BLANK,
  clockStopped,
  inMargin,
  inTerminus,
  pastEdge,
  rodeAway,
  storyBranch,
  storyLineFor,
  SKINS,
  STASH_MAX,
  STUFF,
  TILE,
  TOOTH,
  TOWNS,
  WORLD_HASH,
  T_GLOW,
  T_MUD,
  T_TOXIC,
  T_VENT,
  USE_R,
  isStuffId,
  questBlurb,
  tiles,
  townAt,
  type Job,
  type NpcId,
} from '../shared/content.ts';
import {
  BASES,
  EQUIP_LABEL,
  EQUIP_SLOTS,
  RARITY_COLOR,
  RARITY_NAME,
  computeStats,
  describe,
  emptyGear,
  itemName,
  sellValue,
  slotFits,
  slotLabel,
  salvageOf,
  type EquipSlot,
  type Gear,
  type ItemInst,
  type Stats,
} from '../shared/items.ts';
import { dist, tryMove } from '../shared/physics.ts';
import { GEN, regionAtTile, tierAtTile } from '../shared/worldgen.ts';
import type { ActorSnap, BoardMsg, CoachMsg, DropSnap, FireMsg, FxSnap, Head, InvMsg, Look, OriginId, QuestState, ShopMsg, SnapMsg, StashMsg, TalkMsg } from '../shared/types.ts';
import { HEADS } from '../shared/types.ts';
import { isMusicOn, isMuted, setMood, setMusic, setMusicOn, setMuted, sfx, sfxBloom, sfxCoin, sfxLoot, sfxSmack, sfxWeapon, type Mood } from './audio.ts';
import { burstFx } from './fx.ts';
import { drawWorld, drawWorldMap, layoutDropLabels, setSeen, type DrawActor, type LabelBox } from './draw.ts';
import { drawPerson } from './sprites.ts';
import { initArt } from './art.ts';
import { SURVIVORS } from '../shared/survivors.ts';
initArt();

type Mode = 'auth' | 'create' | 'play';

const $ = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
};

const canvas = $<HTMLCanvasElement>('view');
const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('No canvas');
const g = ctx;

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const look: Look = { skin: 0, cloth: 0, accent: 0, head: 'tuft', survivor: Math.floor(Math.random() * SURVIVORS.length) };
let origin: OriginId = 'knuckle';
let authMode: 'login' | 'register' = 'login';
let mode: Mode = 'auth';
let ws: WebSocket | null = null;
let loggingOut = false;

let you: SnapMsg['you'] | null = null;
let inv: InvMsg | null = null;
let invAt = 0;
let stats: Stats = computeStats('knuckle', emptyGear());
let actors: ActorSnap[] = [];
let shots: SnapMsg['shots'] = [];
let worldFx: FxSnap[] = [];
let worldNodes: SnapMsg['nodes'] = [];
let worldDrops: DropSnap[] = [];
let worldFires: SnapMsg['fires'] = [];
let others: SnapMsg['others'] = [];
let worldEvent: { x: number; y: number; name: string } | null = null;
const seenFx = new Set<number>();
const seenDrops = new Set<number>();
const ghosts = new Map<number, { x: number; y: number }>();
let predX = 0;
let predY = 0;
let predReady = false;
const keys = new Set<string>();
let aim = 0;
let mouseX = 0;
let mouseY = 0;
let worldMouseX = 0;
let worldMouseY = 0;
let hoverDrop: number | null = null;
let groundTip = false;
let meleeLatch = false;
let shootLatch = false;
let shootHeld = false;
let interactLatch = false;
let typing = false;
let menuOpen = false;
let barSlot = 0;
let guideId: string | null = null;
let camX = 200;
let camY = 180;
let scale = 3;
let shake = 0;
let lastHp = -1;
let lastTabs = -1;
let lastQuest: QuestState | null = null;
let lastStory = -1;
let regionNow = '';
const floats: { x: number; y: number; text: string; color: string; age: number }[] = [];
const toasts: { text: string; age: number }[] = [];
let time = 0;
let lastFrame = performance.now();

// Which panel is open. Only one at a time, except the tooltip.
type Panel = 'talk' | 'pack' | 'shop' | 'stash' | 'board' | 'coach' | 'jobs' | 'sheet' | 'map' | 'fire' | null;
let panel: Panel = null;
let talkNpc: NpcId | null = null;
let shopMsg: ShopMsg | null = null;
let shopAt = 0;
let stashMsg: StashMsg | null = null;
let boardMsg: BoardMsg | null = null;
let coachMsg: CoachMsg | null = null;
let fireId: number | null = null;
let selected: string | null = null;

setMuted(localStorage.getItem('radville-mute') === '1');
setMusicOn(localStorage.getItem('radville-music') !== '0');
refreshMuteLabel();

const PANEL_IDS: Exclude<Panel, null>[] = ['talk', 'pack', 'shop', 'stash', 'board', 'coach', 'jobs', 'sheet', 'map', 'fire'];

function openPanel(next: Panel): void {
  panel = next;
  for (const id of PANEL_IDS) $(id).classList.toggle('hidden', id !== next);
  hideTip();
  if (next !== 'talk') talkNpc = null;
  if (next !== 'fire') fireId = null;
  if (next !== 'pack' && next !== 'shop' && next !== 'stash') selected = null;
  renderPanel();
}

function closePanel(): void {
  openPanel(null);
}

function setMode(next: Mode): void {
  mode = next;
  document.body.className = `mode-${next}`;
  $('auth').classList.toggle('hidden', next !== 'auth');
  $('create').classList.toggle('hidden', next !== 'create');
  $('scrim').classList.toggle('hidden', next === 'play');
  $('hud').classList.toggle('hidden', next !== 'play');
  if (next !== 'play') {
    closePanel();
    menuOpen = false;
    hide($('menu'));
    hide($('dead'));
  }
}

function hide(node: HTMLElement): void {
  node.classList.add('hidden');
}
function show(node: HTMLElement): void {
  node.classList.remove('hidden');
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

function button(label: string, onClick: () => void, cls?: string, disabled = false): HTMLButtonElement {
  const node = el('button', cls, label);
  node.type = 'button';
  node.disabled = disabled;
  node.addEventListener('click', (ev) => {
    ev.stopPropagation();
    onClick();
  });
  return node;
}

function toast(text: string): void {
  toasts.push({ text, age: 0 });
  if (toasts.length > 4) toasts.shift();
  renderToasts();
}

function renderToasts(): void {
  const root = $('toasts');
  root.replaceChildren();
  for (const item of toasts) root.append(el('div', 'toast', item.text));
}

function chatLine(name: string, text: string): void {
  const log = $('chat-log');
  const row = el('div', name === 'The road' ? 'road' : undefined);
  row.append(el('b', undefined, name), document.createTextNode(` ${text}`));
  log.append(row);
  while (log.children.length > 9) log.firstChild?.remove();
}

async function api(path: string, body?: unknown): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const res = await fetch(path, {
    method: body ? 'POST' : 'GET',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

function send(msg: unknown): void {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

// ---------------------------------------------------------------------------
// The wire.

function connect(): void {
  if (ws && ws.readyState <= 1) ws.close();
  loggingOut = false;
  hide($('conn'));
  const socket = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`);
  ws = socket;
  socket.addEventListener('open', () => setMode('play'));
  socket.addEventListener('message', (ev) => onMessage(String(ev.data)));
  socket.addEventListener('close', (ev) => {
    if (loggingOut || socket !== ws) return;
    you = null;
    inv = null;
    predReady = false;
    const text =
      ev.code === 4003
        ? ev.reason || 'Radville is full.'
        : ev.code === 4000
          ? 'Signed in somewhere else.'
          : ev.code === 4004
            ? 'That character was cleared.'
            : ev.code === 4001
              ? 'Your seat was pulled.'
              : ev.code === 4005
                ? 'Password changed. Sign in again.'
                : 'The road swallowed the connection.';
    $('conn-text').textContent = text;
    show($('conn'));
    if (ev.code === 4004) {
      hide($('conn'));
      setMode('create');
    }
    if (ev.code === 4001 || ev.code === 4005) {
      hide($('conn'));
      setMode('auth');
    }
  });
}

function onMessage(raw: string): void {
  const msg = JSON.parse(raw) as { t?: string };
  switch (msg.t) {
    case 'snap':
      applySnap(msg as SnapMsg);
      break;
    case 'inv':
      applyInv(msg as InvMsg);
      break;
    case 'map': {
      const seen = (msg as { seen: string }).seen;
      const bin = atob(seen);
      const bits = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bits[i] = bin.charCodeAt(i);
      setSeen(bits);
      break;
    }
    case 'toast': {
      const text = (msg as { text: string }).text;
      toast(text);
      if (/^(Bought|Sold)|takes (the|one|\d+)/.test(text)) sfx('buy');
      if (/^(The (cooler cell|clapper|keel|hiss|tooth|cushion)|Home is|Job done|You could find this again)/.test(text) || /is yours/.test(text)) sfx('quest');
      break;
    }
    case 'fx':
      for (const floater of (msg as { floats: { x: number; y: number; text: string; color: string }[] }).floats) floats.push({ ...floater, age: 0 });
      break;
    case 'talk':
      openTalk(msg as TalkMsg);
      break;
    case 'shop':
      shopMsg = msg as ShopMsg;
      shopAt = performance.now();
      if (panel !== 'shop') openPanel('shop');
      else renderPanel();
      break;
    case 'stash':
      stashMsg = msg as StashMsg;
      if (panel !== 'stash') openPanel('stash');
      else renderPanel();
      break;
    case 'board':
      boardMsg = msg as BoardMsg;
      if (panel !== 'board') openPanel('board');
      else renderPanel();
      break;
    case 'coach':
      coachMsg = msg as CoachMsg;
      openPanel('coach');
      break;
    case 'fire':
      openFire(msg as FireMsg);
      break;
    case 'chat':
      chatLine(String((msg as { name: string }).name), String((msg as { text: string }).text));
      break;
    case 'hello':
      if ((msg as { world?: string }).world !== WORLD_HASH) {
        // An old game.js against a new server. The walls would not line up.
        $('conn-text').textContent = 'The town changed while you were out. Reload to catch up.';
        show($('conn'));
        loggingOut = true;
        ws?.close();
      }
      break;
    case 'chatlog':
      $('chat-log').replaceChildren();
      for (const line of (msg as { lines: { name: string; text: string }[] }).lines) chatLine(line.name, line.text);
      break;
  }
}

function applyInv(msg: InvMsg): void {
  const first = !inv;
  inv = msg;
  invAt = performance.now();
  stats = computeStats(msg.origin, msg.gear);
  if (guideId && !msg.marks.includes(guideId)) guideId = null;
  if (lastQuest && lastQuest !== msg.quest) sfx('quest');
  if (lastStory >= 0 && lastStory !== msg.story) sfx('quest');
  lastQuest = msg.quest;
  lastStory = msg.story;
  if (selected && !msg.bag.some((item) => item.uid === selected)) selected = null;
  renderHud();
  renderHands();
  renderHotbar();
  renderTracker();
  if (panel) renderPanel();
  if (first) $('who-name').textContent = msg.name;
}

function noteFx(list: FxSnap[]): void {
  const live = new Set(list.map((item) => item.id));
  for (const item of list) {
    if (seenFx.has(item.id)) continue;
    seenFx.add(item.id);
    if (!reduce) burstFx(item.x, item.y, item.kind);
    const reach = (item.r || 16) + 80;
    const d = Math.hypot(predX - item.x, predY - item.y);
    if (d < reach + 80 && (item.kind === 'pop' || item.kind === 'bell' || item.kind === 'suck' || item.kind === 'laser')) sfxBloom(item.kind);
    if (item.kind === 'death' && item.boss && d < 400) {
      // A boss going up: rattle the screen and let the bloom sound go off.
      shake = Math.max(shake, 10 * (1 - d / 400) + 3);
      sfxBloom('pop');
    }
    if (d < reach && item.kind !== 'loot' && item.kind !== 'death') {
      const fall = 1 - d / reach;
      const power = item.kind === 'pop' ? 9 : item.kind === 'bell' ? 6 : item.kind === 'suck' ? 5 : item.kind === 'laser' ? 4 : 2;
      shake = Math.max(shake, power * fall);
    }
  }
  for (const id of seenFx) if (!live.has(id)) seenFx.delete(id);
  worldFx = list;
}

function applySnap(snap: SnapMsg): void {
  const prevHp = lastHp;
  you = snap.you;
  actors = snap.actors;
  shots = snap.shots;
  noteFx(snap.fx ?? []);
  worldNodes = snap.nodes;
  worldFires = snap.fires;
  others = snap.others;
  worldEvent = snap.event ?? null;
  // New good things on the ground ring once.
  const live = new Set<number>();
  for (const drop of snap.drops) {
    live.add(drop.id);
    if (seenDrops.has(drop.id)) continue;
    seenDrops.add(drop.id);
    if (predReady && drop.rarity && drop.rarity !== 'plain' && !drop.held) sfxLoot(drop.rarity);
  }
  for (const id of seenDrops) if (!live.has(id)) seenDrops.delete(id);
  worldDrops = snap.drops;
  if (!predReady || snap.you.dead) {
    predX = snap.you.x;
    predY = snap.you.y;
    predReady = true;
  } else {
    const dx = snap.you.x - predX;
    const dy = snap.you.y - predY;
    if (dx * dx + dy * dy > 36 * 36) {
      predX = snap.you.x;
      predY = snap.you.y;
    } else {
      predX += dx * 0.35;
      predY += dy * 0.35;
    }
  }
  for (const actor of actors) {
    if (actor.id === snap.you.id) continue;
    if (!ghosts.has(actor.id)) ghosts.set(actor.id, { x: actor.x, y: actor.y });
  }
  if (prevHp >= 0 && snap.you.hp < prevHp) {
    const lost = prevHp - snap.you.hp;
    shake = Math.max(shake, lost >= 6 ? 6 : 2);
    sfx(snap.you.hp <= 0 ? 'death' : 'hurt');
  }
  if (lastTabs >= 0 && snap.you.tabs > lastTabs && panel !== 'shop') sfxCoin();
  lastTabs = snap.you.tabs;
  noteSmacks(snap.actors);
  lastHp = snap.you.hp;
  renderVitals();
  $('dead').classList.toggle('hidden', !snap.you.dead);
  $('dead-line').textContent = inv?.home ? 'The fire still knows you.' : 'The diner smell pulls you back.';
  // Walk off and the panel closes behind you.
  if (panel === 'talk' && talkNpc && dist(predX, predY, NPCS[talkNpc].x, NPCS[talkNpc].y) > 72) closePanel();
  if (panel === 'fire') {
    const fire = worldFires.find((item) => item.id === fireId);
    if (!fire || dist(predX, predY, fire.x, fire.y) > 72) closePanel();
  }
  if (panel === 'shop' && shopMsg && dist(predX, predY, NPCS[shopMsg.npc].x, NPCS[shopMsg.npc].y) > SHOP_R + 24) closePanel();
  if (panel === 'board' && boardMsg && dist(predX, predY, NPCS[boardMsg.npc].x, NPCS[boardMsg.npc].y) > 96) closePanel();
  if (panel === 'stash' && !nearLocker()) closePanel();
  if (panel === 'coach' && !TOWNS.some((town) => dist(predX, predY, town.post.x, town.post.y) < 80)) closePanel();
}

function nearLocker(): boolean {
  return TOWNS.some((town) => dist(predX, predY, town.locker.x, town.locker.y) <= USE_R + 20);
}

function nearVendor(): NpcId | null {
  for (const npc of ['peck', 'moss', 'sully', 'tuck'] as const) if (dist(predX, predY, NPCS[npc].x, NPCS[npc].y) <= SHOP_R) return npc;
  return null;
}

// ---------------------------------------------------------------------------
// HUD.

function renderHud(): void {
  if (!inv) return;
  $('who-name').textContent = inv.name;
  $('who-origin').textContent = ORIGINS.find((item) => item.id === inv?.origin)?.name ?? '';
}

function renderVitals(): void {
  if (!you) return;
  $('hp-fill').style.width = `${Math.max(0, (you.hp / you.maxHp) * 100)}%`;
  $('hp-text').textContent = `${you.hp} / ${you.maxHp}`;
  $('tabs').textContent = `${you.tabs} tabs`;
  $('mend').textContent = [you.buff > 0 ? `Humming, ${you.buff}s.` : '', hazardLine() || mendLabel()].filter(Boolean).join(' ');
  // Cooldown shades on the hands.
  const melee = stats.melee;
  const gun = stats.ranged;
  for (const hand of document.querySelectorAll<HTMLElement>('.hand')) {
    const kind = hand.dataset.kind;
    const shade = hand.querySelector<HTMLElement>('.cdv');
    if (!shade) continue;
    const frac = kind === 'melee' ? you.meleeCd / Math.max(0.05, melee.cd) : kind === 'ranged' && gun ? you.shootCd / Math.max(0.05, gun.cd) : 0;
    shade.style.height = `${Math.max(0, Math.min(1, frac)) * 100}%`;
  }
}

function iconUrl(id: string): string {
  return `/icons/${id}.png`;
}

function iconEl(id: string): HTMLImageElement {
  const img = el('img', 'ico');
  img.alt = '';
  img.src = iconUrl(id);
  img.draggable = false;
  return img;
}

function renderHands(): void {
  if (!inv) return;
  const root = $('hands');
  root.replaceChildren();
  for (const slot of ['hand', 'sling'] as const) {
    const item = inv.gear[slot];
    const box = el('div', 'hand');
    const kind = item ? BASES[item.base].kind : 'melee';
    box.dataset.kind = !item && slot === 'sling' ? 'none' : kind;
    if (slot === 'hand') box.classList.add('on');
    box.append(el('span', 'key', slot === 'hand' ? 'Hand' : 'Q'));
    if (item) {
      box.append(iconEl(item.base));
      box.style.borderColor = item.rarity === 'plain' ? '' : RARITY_COLOR[item.rarity];
      bindItemTip(box, item, 'worn');
    } else box.append(el('span', 'fists', slot === 'hand' ? 'fists' : '—'));
    box.append(el('span', 'cdv'));
    root.append(box);
  }
}

const SLOT_NAME: Record<string, string> = { scrap: 'Scrap', wire: 'Wire', rag: 'Rag', bulb: 'Bulb', bandage: 'Wrap', stim: 'Stim', camp: 'Fire', grit: 'Grit', drip: 'Drip', slag: 'Slag' };
let hotKey = '';
function renderHotbar(): void {
  if (!inv) return;
  const key = `${barSlot}|${inv.bar.join(',')}|${JSON.stringify(inv.stacks)}`;
  if (key === hotKey) return;
  hotKey = key;
  const root = $('hotbar');
  root.replaceChildren();
  for (let i = 0; i < 6; i++) {
    const id = inv.bar[i] ?? null;
    const b = el('button');
    b.type = 'button';
    if (i === barSlot) b.className = 'on';
    b.append(el('span', 'idx', String(i + 1)));
    if (id && isStuffId(id)) {
      b.append(iconEl(id));
      b.append(el('span', 'qty', String(inv.stacks[id] ?? 0)));
      bindTextTip(b, STUFF[id].name, STUFF[id].blurb + (STUFF[id].heal ? ` Mends ${STUFF[id].heal}.` : ''));
    } else b.title = 'Empty. Put stuff here from your pockets.';
    void SLOT_NAME;
    b.addEventListener('click', () => {
      if (barSlot === i) send({ t: 'barUse', slot: i });
      else {
        barSlot = i;
        hotKey = '';
        renderHotbar();
      }
    });
    b.addEventListener('contextmenu', (ev) => {
      ev.preventDefault();
      send({ t: 'barSet', slot: i });
    });
    root.append(b);
  }
}

function jobPlace(job: Job): { x: number; y: number } | null {
  if (job.kind === 'look') {
    const mark = MARKS.find((item) => item.id === job.target);
    return mark ? { x: mark.x, y: mark.y } : null;
  }
  if (job.kind === 'boss') {
    const spawn = GEN.spawns.find((item) => item.kind === job.target && item.boss) ?? ENEMY_SPAWNS.find((item) => item.kind === job.target);
    return spawn ? { x: spawn.tx * TILE + 8, y: spawn.ty * TILE + 8 } : null;
  }
  return null;
}

/** Where the story wants you next, for the map and the glint. */
function storyPlace(): { x: number; y: number } | null {
  if (!inv || !storyOpen(inv.quest)) return null;
  const step = STORY[inv.story];
  if (!step || step.kind === 'done') return null;
  if (step.kind === 'spot') return step.target && STORY_SPOTS[step.target] ? wayTo(STORY_SPOTS[step.target]) : null;
  if (step.kind === 'loud') {
    const mark = MARKS.find((item) => item.id === step.target);
    return mark ? wayTo(mark) : null;
  }
  if (step.kind === 'kill') {
    if (step.target === 'wanderer') return worldEvent ? { x: worldEvent.x, y: worldEvent.y } : null;
    const spawn =
      ENEMY_SPAWNS.find((item) => item.kind === step.target) ??
      GEN.spawns.find((item) => item.kind === step.target) ??
      GEN.under.find((item) => item.kind === step.target) ??
      GEN.edge.find((item) => item.kind === step.target);
    return spawn ? wayTo({ x: spawn.tx * TILE + 8, y: spawn.ty * TILE + 8 }) : null;
  }
  return step.npc ? wayTo(NPCS[step.npc]) : null;
}

/** Under the Click Station: the Undertrack, reached only by its stair. */
function inUndertrack(x: number, y: number): boolean {
  return y >= 260 * TILE && !pastEdge(y) && x < 60 * TILE;
}

/** The next door on the way to a place that is sealed off from here, or the place itself. */
function wayTo(to: { x: number; y: number }): { x: number; y: number } {
  const at = { x: predX, y: predY };
  const zone = (p: { x: number; y: number }) => (inTerminus(p.x, p.y) ? 'terminus' : inMargin(p.x, p.y) ? 'margin' : inUndertrack(p.x, p.y) ? 'under' : 'top');
  const from = zone(at);
  const goal = zone(to);
  if (from === goal) return { x: to.x, y: to.y };
  if (from === 'terminus') return LAST_STOP.terminusCoach;
  if (from === 'margin') return goal === 'terminus' ? LAST_STOP.coach : EDGE.back;
  if (from === 'under') return STAIRS.up;
  return goal === 'under' ? STAIRS.down : EDGE.out;
}

function renderTracker(): void {
  if (!inv) return;
  $('quest').textContent =
    inv.side === 'coach'
      ? 'Halfway to Halt, on the dull track. The cushion is still in the coach.'
      : inv.side === 'coach_got'
        ? 'You have the cushion. Vetch, in Halt, will pay.'
        : storyOpen(inv.quest)
          ? (STORY[inv.story]?.hint ?? questBlurb(inv.quest))
          : questBlurb(inv.quest);
  const root = $('track-jobs');
  root.replaceChildren();
  for (const job of inv.jobs) {
    const done = job.have >= job.need;
    const row = el('div', done ? 'track done' : 'track');
    row.append(el('b', undefined, done ? 'Done. Any board pays.' : `${job.have} / ${job.need}`), el('span', undefined, ` ${job.text}`));
    root.append(row);
  }
}

function hazardLine(): string {
  const tile = tiles[Math.floor(predY / TILE)]?.[Math.floor(predX / TILE)];
  if (tile === T_TOXIC) return 'The pool is on your boots.';
  if (tile === T_GLOW && !(inv && clockStopped(inv.quest, inv.story) && predX >= LEAK_EDGE.x0 * TILE && predX < LEAK_EDGE.x1 * TILE && predY < LEAK_EDGE.y1 * TILE)) return 'The glow is faint, and it is counting.';
  if (tile === T_BLANK) return 'Nobody painted this bit.';
  if (tile === T_VENT) return 'The vent is breathing on you.';
  if (tile === T_MUD) return 'The mud has opinions about your boots.';
  return '';
}

function mendLabel(): string {
  if (!you || you.dead || you.hp >= you.maxHp) return '';
  const town = townAt(predX, predY);
  if (town) return `${town === 'radville' ? 'The diner' : TOWNS.find((item) => item.id === town)?.name} is mending you.`;
  if (worldFires.some((fire) => dist(predX, predY, fire.x, fire.y) <= MEND_R)) return 'The fire is mending you.';
  return '';
}

function renderWhere(): void {
  const tx = Math.floor(predX / TILE);
  const ty = Math.floor(predY / TILE);
  const region = regionAtTile(tx, ty);
  const tier = tierAtTile(tx, ty);
  $('where-name').textContent = region.name;
  const tierText = region.biome === 'town' || region.id === 'radville' ? 'safe-ish' : `danger ${tier}`;
  const tierEl = $('where-tier');
  tierEl.textContent = tierText;
  tierEl.style.color = tier >= 7 ? '#e07058' : tier >= 5 ? '#e07a3d' : tier >= 3 ? '#e0b15a' : '#a39886';
  if (region.id !== regionNow) {
    const first = regionNow === '';
    regionNow = region.id;
    if (!first) {
      const banner = $('banner');
      $('banner-name').textContent = region.name;
      $('banner-tier').textContent = tierText;
      banner.classList.remove('hidden');
      // Restart the fade.
      banner.style.animation = 'none';
      void banner.offsetWidth;
      banner.style.animation = '';
    }
  }
}

// ---------------------------------------------------------------------------
// Tooltips.

function showTipNode(node: HTMLElement, x: number, y: number): void {
  const tip = $('tip');
  tip.replaceChildren(node);
  tip.classList.remove('hidden');
  const w = tip.offsetWidth;
  const h = tip.offsetHeight;
  tip.style.left = `${Math.max(6, Math.min(x + 16, window.innerWidth - w - 8))}px`;
  tip.style.top = `${Math.max(6, Math.min(y + 16, window.innerHeight - h - 8))}px`;
}

function showTip(name: string, blurb: string, x: number, y: number): void {
  const box = el('div');
  box.append(el('b', undefined, name), el('span', 'blurb', blurb));
  showTipNode(box, x, y);
}

function hideTip(): void {
  $('tip').classList.add('hidden');
}

function bindTextTip(node: HTMLElement, name: string, blurb: string): void {
  node.addEventListener('mouseenter', (ev) => showTip(name, blurb, ev.clientX, ev.clientY));
  node.addEventListener('mousemove', (ev) => showTip(name, blurb, ev.clientX, ev.clientY));
  node.addEventListener('mouseleave', hideTip);
}

/** Where a piece would go if equipped now. Mirrors the server's pick. */
function targetSlot(item: ItemInst, gear: Gear): EquipSlot {
  const base = BASES[item.base];
  if (base.slot !== 'weapon') return base.slot as EquipSlot;
  if (!gear.hand) return 'hand';
  if (!gear.sling) return 'sling';
  if (BASES[gear.hand.base]?.kind === base.kind) return 'hand';
  if (BASES[gear.sling.base]?.kind === base.kind) return 'sling';
  return 'hand';
}

function compareLines(item: ItemInst): { text: string; cls: string }[] {
  if (!inv) return [];
  const slot = targetSlot(item, inv.gear);
  const after = { ...inv.gear, [slot]: item };
  const now = stats;
  const next = computeStats(inv.origin, after);
  const out: { text: string; cls: string }[] = [];
  const diff = (label: string, a: number, b: number, fmt = (v: number) => String(Math.round(v))) => {
    if (Math.abs(a - b) < 0.01) return;
    const up = b > a;
    out.push({ text: `${label} ${fmt(b)} (${up ? '+' : ''}${fmt(b - a)})`, cls: up ? 'up' : 'down' });
  };
  diff('Melee damage', now.melee.dmg, next.melee.dmg);
  diff('Swings/s', 1 / now.melee.cd, 1 / next.melee.cd, (v) => v.toFixed(1));
  diff('Shot damage', now.ranged?.dmg ?? 0, next.ranged?.dmg ?? 0);
  diff('Shots/s', now.ranged ? 1 / now.ranged.cd : 0, next.ranged ? 1 / next.ranged.cd : 0, (v) => v.toFixed(1));
  diff('Soak', now.armor, next.armor);
  diff('Health', now.maxHp, next.maxHp);
  diff('Speed', now.speed, next.speed);
  diff('Hit twice as hard %', Math.max(now.melee.crit, now.ranged?.crit ?? 0), Math.max(next.melee.crit, next.ranged?.crit ?? 0));
  diff('Mend', now.mend, next.mend);
  diff('Finds %', now.find, next.find);
  diff('Tabs found %', now.tabs, next.tabs);
  return out;
}

function itemTip(item: ItemInst, where: 'bag' | 'worn' | 'shop' | 'ground', price?: number): HTMLElement {
  const base = BASES[item.base];
  const box = el('div');
  const name = el('b', undefined, itemName(item));
  name.style.color = RARITY_COLOR[item.rarity];
  box.append(name);
  box.append(el('span', 'kind', `${RARITY_NAME[item.rarity]} · ${slotLabel(base)} · found at danger ${item.tier}`));
  const lines = describe(item);
  const own = item.affixes.length;
  lines.forEach((line, i) => box.append(el('span', i >= lines.length - own ? 'affix' : 'stat', line)));
  box.append(el('span', 'blurb', base.blurb));
  if (where !== 'worn' && inv) {
    const slot = targetSlot(item, inv.gear);
    const worn = inv.gear[slot];
    const cmp = el('div', 'cmp');
    cmp.append(el('span', 'fine', worn ? `If it replaces the ${itemName(worn)}:` : `If worn (${EQUIP_LABEL[slot].toLowerCase()}):`));
    const lines2 = compareLines(item);
    if (!lines2.length) cmp.append(el('span', 'fine', 'No change to your numbers.'));
    for (const line of lines2) cmp.append(el('span', line.cls, line.text));
    box.append(cmp);
  }
  if (price != null) box.append(el('span', 'price', `${price} tabs`));
  else if (where === 'bag') box.append(el('span', 'price', `Sells for ${sellValue(item)} · breaks into ${salvageText(item)}`));
  return box;
}

function salvageText(item: ItemInst): string {
  if (item.rarity === 'relic') return 'nothing (relics hold)';
  return (
    Object.entries(salvageOf(item))
      .filter(([, n]) => n)
      .map(([id, n]) => `${n} ${STUFF[id as keyof typeof STUFF].name.toLowerCase()}`)
      .join(', ') || 'nothing'
  );
}

function bindItemTip(node: HTMLElement, item: ItemInst, where: 'bag' | 'worn' | 'shop' | 'ground', price?: number): void {
  const showIt = (ev: MouseEvent) => showTipNode(itemTip(item, where, price), ev.clientX, ev.clientY);
  node.addEventListener('mouseenter', showIt);
  node.addEventListener('mousemove', showIt);
  node.addEventListener('mouseleave', hideTip);
}

function slotEl(item: ItemInst | null, opts: { where: 'bag' | 'worn' | 'shop'; price?: number; cant?: boolean; onClick?: (ev: MouseEvent) => void; onRight?: () => void; drag?: string }): HTMLElement {
  const node = el('div', 'slot');
  if (!item) {
    node.classList.add('empty');
    return node;
  }
  node.classList.add(`r-${item.rarity}`);
  if (opts.cant) node.classList.add('cant');
  if (selected === item.uid) node.classList.add('sel');
  node.append(iconEl(item.base));
  node.append(el('span', 'tier', String(item.tier)));
  if (opts.price != null) node.append(el('span', 'price', String(opts.price)));
  bindItemTip(node, item, opts.where, opts.price);
  if (opts.onClick) node.addEventListener('click', (ev) => opts.onClick?.(ev));
  if (opts.onRight)
    node.addEventListener('contextmenu', (ev) => {
      ev.preventDefault();
      opts.onRight?.();
    });
  if (opts.drag) {
    node.draggable = true;
    node.addEventListener('dragstart', (ev) => {
      hideTip();
      ev.dataTransfer?.setData('text/plain', opts.drag ?? '');
      if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
    });
  }
  return node;
}

// ---------------------------------------------------------------------------
// Panels.

function renderPanel(): void {
  if (!panel) return;
  if (panel === 'pack') renderPack();
  if (panel === 'shop') renderShop();
  if (panel === 'stash') renderStash();
  if (panel === 'board') renderBoard();
  if (panel === 'coach') renderCoach();
  if (panel === 'jobs') renderJobs();
  if (panel === 'sheet') renderSheet();
  if (panel === 'map') renderMapMarks();
}

function openTalk(msg: TalkMsg): void {
  talkNpc = msg.npc;
  $('talk-name').textContent = msg.name;
  $('talk-text').textContent = msg.text;
  const choices = $('talk-choices');
  choices.replaceChildren();
  for (const choice of msg.choices) {
    choices.append(
      button(choice.label, () => {
        if (choice.id === 'leave') {
          closePanel();
          return;
        }
        send({ t: 'talk', npc: msg.npc, choice: choice.id });
      }),
    );
  }
  panel = 'talk';
  for (const id of PANEL_IDS) $(id).classList.toggle('hidden', id !== 'talk');
  talkNpc = msg.npc;
}

function renderDoll(): void {
  if (!inv) return;
  const root = $('doll');
  root.replaceChildren();
  for (const slot of EQUIP_SLOTS) {
    const item = inv.gear[slot];
    const box = el('div', 'doll-slot');
    box.style.gridArea = slot;
    box.append(el('span', 'lbl', EQUIP_LABEL[slot]));
    if (item) {
      box.classList.add('full', `r-${item.rarity}`);
      box.append(iconEl(item.base));
      bindItemTip(box, item, 'worn');
      box.draggable = true;
      box.addEventListener('dragstart', (ev) => {
        hideTip();
        ev.dataTransfer?.setData('text/plain', `worn:${slot}`);
      });
      box.addEventListener('click', () => send({ t: 'unequip', slot }));
      box.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        send({ t: 'unequip', slot });
      });
    }
    box.addEventListener('dragover', (ev) => ev.preventDefault());
    box.addEventListener('drop', (ev) => {
      ev.preventDefault();
      const data = ev.dataTransfer?.getData('text/plain') ?? '';
      if (data.startsWith('item:')) {
        const uid = data.slice(5);
        const piece = inv?.bag.find((it) => it.uid === uid);
        if (!piece || !slotFits(BASES[piece.base], slot)) {
          toast('That does not go there.');
          return;
        }
        send({ t: 'equip', uid, slot });
      }
      if (data === 'worn:hand' && slot === 'sling') send({ t: 'cycle' });
      if (data === 'worn:sling' && slot === 'hand') send({ t: 'cycle' });
    });
    root.append(box);
  }
  const s = stats;
  const statsEl = $('doll-stats');
  statsEl.replaceChildren();
  const row = (label: string, value: string) => {
    const line = el('div', 'rowline');
    line.append(el('span', undefined, label), el('b', undefined, value));
    statsEl.append(line);
  };
  row('Health', String(s.maxHp));
  row('Soak', String(s.armor));
  row('Swing', `${s.melee.dmg} × ${(1 / s.melee.cd).toFixed(1)}/s`);
  row('Shot', s.ranged ? `${s.ranged.dmg} × ${(1 / s.ranged.cd).toFixed(1)}/s` : 'nothing');
  row('Speed', String(Math.round(s.speed)));
  if (s.glow) row('Glow shrug', String(s.glow));
  if (s.mend) row('Mend', `${s.mend} / 3s`);
  if (s.thorns) row('Bites back', String(s.thorns));
  if (s.find) row('Finds', `+${s.find}%`);
  if (s.tabs) row('Tabs found', `+${s.tabs}%`);
}

function renderPack(): void {
  if (!inv) return;
  const me = inv;
  $('pack-count').textContent = `${me.bag.length} / ${BAG_MAX} pieces`;
  renderDoll();
  const bag = $('bag');
  bag.replaceChildren();
  const vendor = nearVendor();
  for (let i = 0; i < BAG_MAX; i++) {
    const item = me.bag[i] ?? null;
    const node = slotEl(item, {
      where: 'bag',
      drag: item ? `item:${item.uid}` : undefined,
      onClick: (ev) => {
        if (!item) return;
        if (ev.shiftKey && vendor) return send({ t: 'sell', uid: item.uid });
        selected = selected === item.uid ? null : item.uid;
        renderPack();
      },
      onRight: () => item && send({ t: 'equip', uid: item.uid }),
    });
    if (item) node.addEventListener('dblclick', () => send({ t: 'equip', uid: item.uid }));
    bag.append(node);
  }
  bag.ondragover = (ev) => ev.preventDefault();
  bag.ondrop = (ev) => {
    ev.preventDefault();
    const data = ev.dataTransfer?.getData('text/plain') ?? '';
    if (data.startsWith('worn:')) send({ t: 'unequip', slot: data.slice(5) });
  };
  const actions = $('pack-actions');
  actions.replaceChildren();
  const sel = me.bag.find((item) => item.uid === selected);
  if (sel) {
    const nameEl = el('span', 'sel-name', itemName(sel));
    nameEl.style.color = RARITY_COLOR[sel.rarity];
    actions.append(nameEl);
    actions.append(button('Wear / hold', () => send({ t: 'equip', uid: sel.uid })));
    if (vendor) actions.append(button(`Sell · ${sellValue(sel)}`, () => send({ t: 'sell', uid: sel.uid }), 'soft'));
    actions.append(button('Break down', () => send({ t: 'salvage', uid: sel.uid }), 'soft', sel.rarity === 'relic'));
    actions.append(button('Drop', () => send({ t: 'dropGear', uid: sel.uid }), 'soft'));
  } else {
    actions.append(el('span', 'fine', 'Click a piece for choices. Right-click or double-click wears it. Drag onto the doll works too.'));
    if (vendor && me.bag.some((item) => item.rarity === 'plain')) actions.append(button('Sell all plain', () => send({ t: 'sellPlain' }), 'soft'));
  }

  const stuffList = $('stuff-list');
  stuffList.replaceChildren();
  let any = false;
  for (const item of Object.values(STUFF)) {
    const count = me.stacks[item.id] ?? 0;
    if (count <= 0) continue;
    any = true;
    const row = el('div', 'line');
    row.append(iconEl(item.id));
    const label = el('div', 'grow');
    label.append(el('span', undefined, `${item.name} × ${count}`));
    row.append(label);
    bindTextTip(row, item.name, item.blurb + (item.heal ? ` Mends ${item.heal}.` : ''));
    const onBar = me.bar.indexOf(item.id);
    row.append(button(onBar === barSlot ? `Off ${barSlot + 1}` : `To ${barSlot + 1}`, () => (onBar === barSlot ? send({ t: 'barSet', slot: barSlot }) : send({ t: 'barSet', slot: barSlot, id: item.id }))));
    if (item.heal) row.append(button('Use', () => send({ t: 'use', id: item.id }), undefined, (you?.hp ?? 0) >= (you?.maxHp ?? 0)));
    if (vendor) row.append(button(`Sell ${item.sell}`, () => send({ t: 'sellStuff', id: item.id })));
    row.append(button('Drop', () => send({ t: 'dropStuff', id: item.id })));
    stuffList.append(row);
  }
  if (!any) stuffList.append(el('p', 'fine', 'Nothing loose. Piles and crates are on the ground.'));

  const make = $('make-list');
  make.replaceChildren();
  for (const recipe of RECIPES) {
    const row = el('div', 'line');
    row.append(iconEl(recipe.out));
    const outName = recipe.kind === 'gear' ? (BASES[recipe.out]?.name ?? recipe.out) : isStuffId(recipe.out) ? STUFF[recipe.out].name : recipe.out;
    const parts = Object.entries(recipe.need)
      .filter((entry): entry is [keyof typeof STUFF, number] => entry[1] != null && entry[1] > 0)
      .map(([id, count]) => `${count} ${STUFF[id].name.toLowerCase()}`);
    const label = el('div', 'grow');
    label.append(el('span', undefined, outName), el('div', 'fine', parts.join(', ')));
    row.append(label);
    const ready = Object.entries(recipe.need).every(([id, count]) => (me.stacks[id] ?? 0) >= (count ?? 0));
    row.append(button(ready ? 'Make' : 'Short', () => send({ t: 'craft', id: recipe.id }), undefined, !ready));
    make.append(row);
  }
}

function renderShop(): void {
  if (!inv || !shopMsg) return;
  const me = inv;
  const shop = shopMsg;
  $('shop-title').textContent = shop.npc === 'moss' ? "Moss's loud toys" : shop.npc === 'sully' ? "Sully's far things" : shop.npc === 'tuck' ? "Tuck's old tickets" : "Peck's junk";
  $('shop-tabs').textContent = `You have ${you?.tabs ?? 0} tabs.`;
  const turns = Math.max(0, Math.round(shop.turns - (performance.now() - shopAt) / 1000));
  $('shop-turn').textContent = `(turns over in ${Math.floor(turns / 60)}:${String(turns % 60).padStart(2, '0')})`;
  const tabs = you?.tabs ?? 0;
  const fill = (root: HTMLElement, list: { item: ItemInst; price: number }[]) => {
    root.replaceChildren();
    for (const entry of list) root.append(slotEl(entry.item, { where: 'shop', price: entry.price, cant: entry.price > tabs, onClick: () => send({ t: 'buy', uid: entry.item.uid }) }));
    if (!list.length) root.append(el('p', 'fine', 'Nothing today.'));
  };
  fill($('shop-stock'), shop.stock);
  fill($('shop-shelf'), shop.shelf);
  const loose = $('shop-stuff');
  loose.replaceChildren();
  for (const entry of shop.stuff) {
    if (!isStuffId(entry.id)) continue;
    const item = STUFF[entry.id];
    const row = el('div', 'line');
    row.append(iconEl(item.id), el('span', 'grow', `${item.name} · ${entry.price} tabs`));
    bindTextTip(row, item.name, item.blurb);
    row.append(button('Buy', () => send({ t: 'buyStuff', id: item.id }), undefined, tabs < entry.price));
    loose.append(row);
  }
  const bag = $('shop-bag');
  bag.replaceChildren();
  for (const item of me.bag) bag.append(slotEl(item, { where: 'bag', price: sellValue(item), onClick: () => send({ t: 'sell', uid: item.uid }) }));
  if (!me.bag.length) bag.append(el('p', 'fine', 'Your pockets are empty.'));
  ($('shop-sell-plain') as HTMLButtonElement).disabled = !me.bag.some((item) => item.rarity === 'plain');
  const mine = $('shop-sell-stuff');
  mine.replaceChildren();
  for (const item of Object.values(STUFF)) {
    const count = me.stacks[item.id] ?? 0;
    if (!count) continue;
    const row = el('div', 'line');
    row.append(iconEl(item.id), el('span', 'grow', `${item.name} × ${count} · ${item.sell} each`));
    row.append(button('Sell one', () => send({ t: 'sellStuff', id: item.id })));
    row.append(button('All', () => send({ t: 'sellStuff', id: item.id, all: true })));
    mine.append(row);
  }
}

function renderStash(): void {
  if (!inv || !stashMsg) return;
  const me = inv;
  const stash = stashMsg;
  $('stash-count').textContent = `${stash.gear.length} / ${STASH_MAX}`;
  const bag = $('stash-bag');
  bag.replaceChildren();
  for (const item of me.bag) bag.append(slotEl(item, { where: 'bag', onClick: () => send({ t: 'stash', dir: 'in', kind: 'gear', id: item.uid }) }));
  if (!me.bag.length) bag.append(el('p', 'fine', 'Nothing in your pockets.'));
  const gear = $('stash-gear');
  gear.replaceChildren();
  for (const item of stash.gear) gear.append(slotEl(item, { where: 'bag', onClick: () => send({ t: 'stash', dir: 'out', kind: 'gear', id: item.uid }) }));
  if (!stash.gear.length) gear.append(el('p', 'fine', 'Nothing yet.'));
  const stuffRow = (root: HTMLElement, stacks: Record<string, number>, dir: 'in' | 'out') => {
    root.replaceChildren();
    for (const [id, count] of Object.entries(stacks)) {
      if (!count || !isStuffId(id)) continue;
      const row = el('div', 'line');
      row.append(iconEl(id), el('span', 'grow', `${STUFF[id].name} × ${count}`));
      row.append(button(dir === 'in' ? 'Stow' : 'Take', () => send({ t: 'stash', dir, kind: 'stuff', id })));
      root.append(row);
    }
  };
  stuffRow($('stash-mine'), me.stacks, 'in');
  stuffRow($('stash-stuff'), stash.stacks, 'out');
}

function jobRow(job: Job, action?: { label: string; run: () => void }, extra?: { label: string; run: () => void }): HTMLElement {
  const row = el('div', 'job');
  row.append(el('div', undefined, job.text));
  const meta = el('div', 'meta');
  const done = job.have >= job.need;
  meta.append(el('b', undefined, `${job.tabs} tabs + a piece`), el('span', undefined, `danger ${job.tier}`), el('span', undefined, done ? 'done' : `${job.have} / ${job.need}`));
  row.append(meta);
  const buttons = el('div', 'rowline tight');
  if (action) buttons.append(button(action.label, action.run));
  if (extra) buttons.append(button(extra.label, extra.run, 'soft'));
  const place = jobPlace(job);
  if (place && job.kind === 'look') buttons.append(button('Point at it', () => pointAtMark(job.target), 'soft'));
  if (buttons.children.length) row.append(buttons);
  return row;
}

function renderBoard(): void {
  if (!inv || !boardMsg) return;
  const board = boardMsg;
  const town = TOWNS.find((item) => item.id === board.town);
  $('board-title').textContent = `${town?.name ?? 'The'} board`;
  const done = $('board-done');
  done.replaceChildren();
  const finished = inv.jobs.filter((job) => job.have >= job.need);
  if (finished.length) done.append(el('h4', undefined, 'Finished'));
  for (const job of finished) done.append(jobRow(job, { label: 'Get paid', run: () => send({ t: 'job', take: job.id, npc: board.npc }) }));
  const offers = $('board-offers');
  offers.replaceChildren();
  const full = inv.jobs.length >= 3;
  for (const job of board.offers) offers.append(jobRow(job, { label: full ? 'Your list is full' : 'Take it', run: () => send({ t: 'job', take: job.id, npc: board.npc }) }));
  if (!board.offers.length) offers.append(el('p', 'fine', 'The board is bare. Come back later.'));
}

function renderCoach(): void {
  if (!coachMsg) return;
  const root = $('coach-stops');
  root.replaceChildren();
  for (const stop of coachMsg.stops) {
    const row = el('div', 'line');
    row.append(el('span', 'grow', stop.known ? `${stop.name} · ${stop.fare} tabs` : `${stop.name} · you have not been`));
    row.append(button('Ride', () => send({ t: 'coach', to: stop.id }), undefined, !stop.known || (you?.tabs ?? 0) < stop.fare));
    root.append(row);
  }
}

function renderJobs(): void {
  if (!inv) return;
  const quest = $('jobs-quest');
  quest.replaceChildren();
  if (storyOpen(inv.quest)) {
    quest.append(el('h4', undefined, 'What you know'));
    for (let i = 0; i <= inv.story && i < STORY.length; i++) {
      if (!storyLineFor(STORY[i], inv.story)) continue;
      if (STORY[i].act) quest.append(el('b', undefined, STORY[i].act));
      if (i < inv.story && STORY[i].learned) quest.append(el('div', 'fine', `· ${STORY[i].learned}`));
    }
    const now = STORY[inv.story];
    if (now) quest.append(el('p', undefined, now.hint));
    quest.append(el('h4', undefined, 'Jobs'));
  } else quest.textContent = questBlurb(inv.quest);
  const root = $('jobs-list');
  root.replaceChildren();
  if (!inv.jobs.length) root.append(el('p', 'fine', 'No jobs. Brine, Vetch, and Ora keep boards.'));
  for (const job of inv.jobs) root.append(jobRow(job, undefined, { label: 'Let it go', run: () => send({ t: 'job', drop: job.id }) }));
}

function renderSheet(): void {
  if (!inv) return;
  $('sheet-name').textContent = inv.name;
  const body = $('sheet-body');
  body.replaceChildren();
  const row = (label: string, value: string) => {
    const line = el('div');
    line.append(el('span', undefined, label), el('b', undefined, value));
    body.append(line);
  };
  const s = stats;
  row('What is wrong with you', ORIGINS.find((item) => item.id === inv?.origin)?.name ?? '');
  row('Health', String(s.maxHp));
  row('Soak', String(s.armor));
  row('Walking speed', String(Math.round(s.speed)));
  row('Swing', `${s.melee.dmg} damage, ${(1 / s.melee.cd).toFixed(1)}/s, reach ${s.melee.range}`);
  row('Shot', s.ranged ? `${s.ranged.dmg} damage, ${(1 / s.ranged.cd).toFixed(1)}/s` : 'nothing that shoots');
  row('Hit twice as hard', `${Math.max(s.melee.crit, s.ranged?.crit ?? 0)}%`);
  row('Stun on hit', `${Math.max(s.melee.stun, s.ranged?.stun ?? 0)}%`);
  row('Health per hit', String(Math.max(s.melee.leech, s.ranged?.leech ?? 0)));
  row('Mend', s.mend ? `${s.mend} every 3s` : 'only at roofs and fires');
  row('Bites back', String(s.thorns));
  row('Shrugs off glow', String(s.glow));
  row('Better finds', `+${s.find}%`);
  row('Tabs found', `+${s.tabs}%`);
  row('Kills', String(inv.tally.kills));
  row('Loud ones put down', String(inv.tally.louds));
  row('Folded up', String(inv.tally.deaths));
  row('Jobs done', String(inv.tally.jobs));
  row('Farthest danger', String(inv.tally.farthest));
  row('Places known', `${inv.marks.length} / ${MARKS.length}`);
  row('Big things put down', inv.tally.bosses.map((kind) => ENEMY_KINDS[kind as keyof typeof ENEMY_KINDS]?.name ?? kind).join(', ') || 'none yet');
}

function pointAtMark(id: string): void {
  guideId = guideId === id ? null : id;
  renderMapMarks();
  toast(guideId ? `Pointing at ${MARKS.find((mark) => mark.id === id)?.name}.` : 'Not pointing at anything.');
}

function renderMapMarks(): void {
  if (!inv) return;
  const root = $('map-marks');
  root.replaceChildren();
  for (const mark of MARKS) {
    if (!inv.marks.includes(mark.id)) continue;
    const b = button(mark.name, () => pointAtMark(mark.id), guideId === mark.id ? 'on' : undefined);
    root.append(b);
  }
  root.append(el('p', 'fine', `${inv.marks.length} of ${MARKS.length} places walked. Gold squares are towns with a coach. Purple is trouble that walks.`));
}

let mapFit = { scale: 1, ox: 0, oy: 0 };
function drawMapPanel(): void {
  if (panel !== 'map' || !inv) return;
  const c = $<HTMLCanvasElement>('map-canvas');
  const w = c.clientWidth;
  const h = c.clientHeight;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (c.width !== Math.floor(w * dpr) || c.height !== Math.floor(h * dpr)) {
    c.width = Math.floor(w * dpr);
    c.height = Math.floor(h * dpr);
  }
  const mc = c.getContext('2d');
  if (!mc) return;
  mc.setTransform(dpr, 0, 0, dpr, 0, 0);
  const jobs = inv.jobs.map(jobPlace).filter((p): p is { x: number; y: number } => Boolean(p));
  const story = storyPlace();
  if (story) jobs.push(story);
  mapFit = drawWorldMap(mc, w, h, { you: { x: predX, y: predY }, others, youId: you?.id ?? -1, knownMarks: inv.marks, guideId, event: worldEvent, jobs, fires: worldFires });
}

function closeFireLocal(): void {
  closePanel();
}

function openFire(msg: FireMsg): void {
  $('fire-name').textContent = 'Campfire';
  $('fire-text').textContent = msg.mine ? (msg.home ? 'This one already knows the way back.' : 'It will burn either way. Home is a choice.') : 'Someone else set this. The heat is free.';
  const choices = $('fire-choices');
  choices.replaceChildren();
  const add = (label: string, choice: string | null): void => {
    choices.append(
      button(label, () => {
        if (choice) send({ t: 'fire', id: msg.id, choice });
        closeFireLocal();
      }),
    );
  };
  add('Warm your hands', 'warm');
  if (msg.mine && !msg.home) add('This is home', 'claim');
  if (msg.mine && msg.home) add('The diner has me', 'forget');
  if (msg.mine) add('Kick it out', 'kick');
  add('Leave it', null);
  openPanel('fire');
  fireId = msg.id;
}

function stitch(): void {
  if (!you || you.dead || !inv) return;
  const have = (['rag', 'bulb', 'bandage', 'stim'] as const).filter((id) => (inv?.stacks[id] ?? 0) > 0).sort((a, b) => (STUFF[a].heal ?? 99) - (STUFF[b].heal ?? 99));
  const pick = have[0];
  if (!pick) {
    toast('Nothing in your pockets will stitch.');
    return;
  }
  send({ t: 'use', id: pick });
}

// ---------------------------------------------------------------------------
// Looking at the ground.

function groundLook(x: number, y: number): { name: string; blurb: string } | null {
  if (mode !== 'play' || !inv) return null;
  let best: { d: number; name: string; blurb: string } | null = null;
  const consider = (px: number, py: number, name: string, blurb: string, r = 18) => {
    if (Math.abs(x - px) > r || Math.abs(y - py) > r) return;
    const d = dist(x, y, px, py);
    if (d > r) return;
    if (!best || d < best.d) best = { d, name, blurb };
  };
  const left = new Map(worldNodes.map((node) => [node.id, node.left]));
  for (const node of ALL_NODES) {
    const n = left.get(node.id);
    if (!n) continue;
    consider(node.x, node.y, STUFF[node.stuff].name, STUFF[node.stuff].blurb);
  }
  for (const box of BOXES) consider(box.x, box.y, box.name, inv.opened.includes(box.id) ? 'Empty. It stays that way.' : 'Still shut.');
  const elapsed = (performance.now() - invAt) / 1000;
  for (const cache of GEN.caches) {
    const wait = (inv.looted[cache.id] ?? 0) - elapsed;
    consider(cache.x, cache.y, cache.name, wait > 0 ? `Empty. Back in about ${Math.max(1, Math.round(wait / 60))} min.` : `Still has something. Danger ${cache.tier}.`);
  }
  for (const fire of worldFires) consider(fire.x, fire.y, fire.home ? 'Your home fire' : fire.mine ? 'Your campfire' : 'Campfire', fire.home ? 'Dying brings you back here.' : 'Stand close and it mends you slowly.');
  if (storyOpen(inv.quest) && inv.story >= STAIR_STEP) consider(STAIRS.down.x, STAIRS.down.y, 'A stair', 'Down, further than the station is tall. E to go.');
  consider(STAIRS.up.x, STAIRS.up.y, 'A stair', 'Up, back to the Click Station. E to go.');
  if (storyOpen(inv.quest) && inv.story >= MARGIN_STEP) consider(EDGE.out.x, EDGE.out.y, 'The end of the road', 'Past it, nobody finished painting. E to step off.', 26);
  consider(EDGE.back.x, EDGE.back.y, 'The edge', 'Back onto the road, by the diner. E to go.', 26);
  consider(LAST_STOP.coach.x, LAST_STOP.coach.y, 'The line', storyBranch(inv.story) === 'ride' && inv.story > BRANCH_START.ride ? 'The coach runs to the end of the line for you. E to flag it.' : 'The rails run west into the paper.', 24);
  consider(LAST_STOP.terminusCoach.x, LAST_STOP.terminusCoach.y, 'The last coach', 'Back to the last platform. E to board.', 28);
  for (const stone of GEN.stones) consider(stone.x, stone.y - 8, 'A stone that hums', 'Touch it (E). You hit harder and walk faster for a minute and a half. It needs ten minutes to hum again.');
  for (const town of TOWNS) {
    consider(town.post.x, town.post.y, 'Coach post', 'E to ride to a town you have walked to.');
    consider(town.locker.x, town.locker.y, 'Your locker', 'Every town has one. It is the same locker.');
  }
  consider(COOLER.x, COOLER.y, 'Cooler cell', 'It hums.');
  consider(CLAPPER.x, CLAPPER.y, 'The clapper', 'It keeps time.');
  consider(KEEL.x, KEEL.y, 'The keel', 'White, and mean.');
  consider(COACH.x, COACH.y, 'The cushion', 'It remembers a person.');
  consider(HISS.x, HISS.y, 'The hiss', 'It wants to conduct you.');
  consider(TOOTH.x, TOOTH.y, 'The tooth', 'Warm, and proud of it.');
  for (const actor of actors) {
    if (actor.kind === 'player' || actor.kind === 'dog' || actor.dead) continue;
    const stats2 = ENEMY_KINDS[actor.kind];
    if (!stats2) continue;
    consider(actor.x, actor.y - 8, actor.name, `${actor.boss ? 'Big. ' : actor.champ ? 'Loud. Better things fall off the loud ones. ' : ''}Danger ${actor.tier ?? stats2.tier}. ${actor.hp} / ${actor.maxHp}.`, 14);
  }
  return best;
}

/** Which drop the mouse is on: its label if one is showing, or the thing itself. */
function dropUnderMouse(wx: number, wy: number, boxes: Map<number, LabelBox>): number | null {
  for (const [id, box] of boxes) if (wx >= box.x && wx <= box.x + box.w && wy >= box.y && wy <= box.y + box.h) return id;
  for (const drop of worldDrops) if (Math.abs(wx - drop.x) < 7 && Math.abs(wy - (drop.y - 4)) < 7) return drop.id;
  return null;
}

// ---------------------------------------------------------------------------
// Input and the frame.

function steering(): boolean {
  return !typing && !menuOpen && panel !== 'map';
}

function sendInput(): void {
  if (mode !== 'play') return;
  const steered = steering();
  send({
    t: 'input',
    up: steered && (keys.has('KeyW') || keys.has('ArrowUp')),
    down: steered && (keys.has('KeyS') || keys.has('ArrowDown')),
    left: steered && (keys.has('KeyA') || keys.has('ArrowLeft')),
    right: steered && (keys.has('KeyD') || keys.has('ArrowRight')),
    aim,
    melee: steered && meleeLatch,
    shoot: steered && (shootHeld || shootLatch),
    interact: steered && interactLatch,
  });
  meleeLatch = false;
  shootLatch = false;
  interactLatch = false;
}

function predict(dt: number): void {
  if (!you || you.dead || !predReady || !steering()) return;
  let dx = (keys.has('KeyD') || keys.has('ArrowRight') ? 1 : 0) - (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0);
  let dy = (keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0) - (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0);
  const mag = Math.hypot(dx, dy);
  if (mag > 0) {
    dx /= mag;
    dy /= mag;
  }
  const mud = tiles[Math.floor(predY / TILE)]?.[Math.floor(predX / TILE)] === T_MUD ? 0.65 : 1;
  const speed = stats.speed * mud * ((you?.buff ?? 0) > 0 ? 1.15 : 1);
  const next = tryMove(predX, predY, dx * speed * dt, dy * speed * dt, PLAYER_RADIUS);
  predX = next.x;
  predY = next.y;
}

function titleActors(now: number): DrawActor[] {
  const list: DrawActor[] = [
    npcActor('brine'),
    npcActor('peck'),
    { id: 9000, kind: 'dog', x: 10 * TILE + 8 + Math.sin(now * 0.4) * 10, y: 38 * TILE + 8, aim: Math.sin(now * 0.4) > 0 ? 0 : Math.PI, hp: 0, maxHp: 0, name: 'Nub', pose: 'walk' },
  ];
  ENEMY_SPAWNS.forEach((spawn, index) => {
    const s = ENEMY_KINDS[spawn.kind];
    list.push({ id: 1000 + index, kind: spawn.kind, x: spawn.tx * TILE + 8, y: spawn.ty * TILE + 8, aim: Math.PI, hp: s.maxHp, maxHp: s.maxHp, name: s.name, pose: 'walk' });
  });
  return list;
}

const NPC_LOOKS: Partial<Record<NpcId, Look>> = {
  vetch: { skin: 2, cloth: 0, accent: 3, head: 'hood' },
  moss: { skin: 5, cloth: 4, accent: 0, head: 'tuft' },
  sully: { skin: 3, cloth: 2, accent: 1, head: 'bald' },
  ora: { skin: 1, cloth: 1, accent: 2, head: 'mohawk' },
  tuck: { skin: 4, cloth: 5, accent: 1, head: 'bald' },
};

function npcActor(id: NpcId): DrawActor {
  const npc = NPCS[id];
  const base: DrawActor = { id: 8000 + Object.keys(NPCS).indexOf(id), kind: 'npc', x: npc.x, y: npc.y, aim: Math.PI / 2, hp: 0, maxHp: 0, name: npc.name, pose: 'idle' };
  if (id === 'brine' || id === 'peck') base.npc = id;
  else if (id === 'lastbrine') base.npc = 'brine';
  else base.look = NPC_LOOKS[id];
  return base;
}

function playActors(): DrawActor[] {
  if (!you || !inv) return titleActors(time);
  // Once you ride with Brine, she is not at the diner for you any more.
  const gone = rodeAway(inv.quest, inv.story);
  const list: DrawActor[] = (Object.keys(NPCS) as NpcId[]).filter((id) => !(gone && id === 'brine')).map(npcActor);
  for (const actor of actors) {
    if (actor.id === you.id) {
      list.push({ ...actor, x: predX, y: predY, aim, pose: you.pose, weapon: inv.gear.hand?.base ?? null, origin: inv.origin, look: inv.look, name: inv.name, dead: you.dead });
      continue;
    }
    const ghost = ghosts.get(actor.id) ?? { x: actor.x, y: actor.y };
    list.push({ ...actor, x: ghost.x, y: ghost.y });
  }
  return list;
}

function lootedSet(): Set<string> {
  const out = new Set<string>();
  if (!inv) return out;
  const elapsed = (performance.now() - invAt) / 1000;
  for (const [id, secs] of Object.entries(inv.looted)) if (secs - elapsed > 0) out.add(id);
  return out;
}

function frame(now: number): void {
  const dt = Math.min(0.05, (now - lastFrame) / 1000);
  lastFrame = now;
  time += dt;
  if (mode === 'play') predict(dt);
  setMood(mode === 'play' && you && !you.dead ? moodHere() : null);
  setMusic(mode === 'play' && you ? (bossNear() ? 'boss' : 'explore') : null);
  for (const [id, ghost] of ghosts) {
    const actor = actors.find((item) => item.id === id);
    if (!actor) {
      ghosts.delete(id);
      continue;
    }
    ghost.x += (actor.x - ghost.x) * Math.min(1, dt * 14);
    ghost.y += (actor.y - ghost.y) * Math.min(1, dt * 14);
  }
  for (const floater of floats) floater.age += dt;
  while (floats.length && floats[0].age > 0.85) floats.shift();
  for (const item of toasts) item.age += dt;
  if (toasts.some((item) => item.age > 3.8)) {
    for (let i = toasts.length - 1; i >= 0; i--) if (toasts[i].age > 3.8) toasts.splice(i, 1);
    renderToasts();
  }
  shake = Math.max(0, shake - dt * 18);

  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (canvas.width !== Math.floor(cssW * dpr) || canvas.height !== Math.floor(cssH * dpr)) {
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
  }
  scale = Math.max(2, Math.min(4, Math.floor(Math.min(cssW, cssH) / 240)));
  const viewW = cssW / scale;
  const viewH = cssH / scale;
  const mapW = MAP_W * TILE;
  const mapH = MAP_H * TILE;
  let focusX = 280 + Math.sin(time * 0.08) * (reduce ? 0 : 70);
  let focusY = 470 + Math.cos(time * 0.06) * (reduce ? 0 : 24);
  if (mode === 'play' && predReady) {
    focusX = predX;
    focusY = predY - 8;
  }
  camX = Math.max(0, Math.min(mapW - viewW, focusX - viewW / 2));
  camY = Math.max(0, Math.min(mapH - viewH, focusY - viewH / 2));
  if (shake > 0) {
    camX += Math.sin(time * 40) * shake;
    camY += Math.cos(time * 35) * shake;
  }

  const rect = canvas.getBoundingClientRect();
  worldMouseX = (mouseX - rect.left) / scale + camX;
  worldMouseY = (mouseY - rect.top) / scale + camY;
  if (mode === 'play' && predReady) aim = Math.atan2(worldMouseY - predY, worldMouseX - predX);
  const labels = keys.has('AltLeft') || keys.has('AltRight');
  const hover = document.elementFromPoint(mouseX, mouseY);
  let dropLabels = layoutDropLabels(mode === 'play' ? worldDrops : [], labels, hoverDrop);
  hoverDrop = hover === canvas && mode === 'play' ? dropUnderMouse(worldMouseX, worldMouseY, dropLabels) : null;
  if (hoverDrop != null && !dropLabels.has(hoverDrop)) dropLabels = layoutDropLabels(worldDrops, labels, hoverDrop);
  if (hover === canvas && hoverDrop == null) {
    const looked = groundLook(worldMouseX, worldMouseY);
    if (looked) {
      showTip(looked.name, looked.blurb, mouseX, mouseY);
      groundTip = true;
    } else if (groundTip) {
      hideTip();
      groundTip = false;
    }
  } else if (hover === canvas && hoverDrop != null) {
    const drop = worldDrops.find((item) => item.id === hoverDrop);
    if (drop) {
      showTip(drop.name ?? (isStuffId(drop.kind) ? STUFF[drop.kind].name : drop.kind === 'tabs' ? `${drop.n} tabs` : drop.kind), drop.held ? 'That fell for somebody else. Give it a moment.' : 'Click to pick it up, or walk over and press E.', mouseX, mouseY);
      groundTip = true;
    }
  } else if (groundTip && !hover?.closest('.slot, .doll-slot, .line, .hand, #hotbar button')) {
    hideTip();
    groundTip = false;
  }
  canvas.style.cursor = hoverDrop != null ? 'pointer' : 'crosshair';

  for (const shot of shots) {
    shot.x += shot.vx * dt;
    shot.y += shot.vy * dt;
  }

  if (mode === 'play') renderWhere();

  drawWorld(g, {
    cssW,
    cssH,
    dpr,
    scale,
    camX,
    camY,
    time,
    actors: mode === 'play' && you ? playActors() : titleActors(time),
    shots: mode === 'play' ? shots : [],
    fx: mode === 'play' ? worldFx : [],
    floats: mode === 'play' ? floats : [],
    nodes: mode === 'play' ? worldNodes : ALL_NODES.map((node) => ({ id: node.id, left: node.n })),
    drops: mode === 'play' ? worldDrops : [],
    fires: mode === 'play' ? worldFires : [],
    guide: guidePoint(),
    storySpot: mode === 'play' ? storyPlace() : null,
    stairOpen: Boolean(inv && storyOpen(inv.quest) && inv.story >= STAIR_STEP),
    edgeOpen: Boolean(inv && storyOpen(inv.quest) && inv.story >= MARGIN_STEP),
    leakQuiet: Boolean(inv && clockStopped(inv.quest, inv.story)),
    knownMarks: inv?.marks ?? ['diner'],
    opened: inv?.opened ?? [],
    looted: lootedSet(),
    aimX: worldMouseX,
    aimY: worldMouseY,
    showAim: mode === 'play' && steering() && panel == null,
    minimap: mode === 'play' && predReady ? { x: predX, y: predY } : null,
    others,
    youId: you?.id ?? -1,
    event: worldEvent,
    labels,
    dropLabels,
    hoverDrop,
    reduce,
  });

  drawMapPanel();
  if (panel === 'shop' && Math.floor(time) !== Math.floor(time - dt)) {
    const turn = $('shop-turn');
    if (shopMsg) {
      const turns = Math.max(0, Math.round(shopMsg.turns - (performance.now() - shopAt) / 1000));
      turn.textContent = `(turns over in ${Math.floor(turns / 60)}:${String(turns % 60).padStart(2, '0')})`;
    }
  }
  if (mode === 'create') drawPreview();
  requestAnimationFrame(frame);
}

function guidePoint(): { x: number; y: number } | null {
  if (mode !== 'play' || !inv || !guideId || !inv.marks.includes(guideId)) return null;
  const mark = MARKS.find((item) => item.id === guideId);
  return mark ? wayTo(mark) : null;
}

function drawPreview(): void {
  const preview = $<HTMLCanvasElement>('preview');
  const pctx = preview.getContext('2d');
  if (!pctx) return;
  pctx.imageSmoothingEnabled = false;
  pctx.setTransform(5, 0, 0, 5, 0, 0);
  pctx.fillStyle = '#1a1814';
  pctx.fillRect(0, 0, 36, 40);
  drawPerson(pctx, 18, 34, { look, pose: 'walk', aim: Math.PI / 2, time, origin, weapon: null });
}

function paintLook(): void {
  $<HTMLElement>('swatch-skin').style.background = SKINS[look.skin];
  $<HTMLElement>('swatch-cloth').style.background = CLOTHS[look.cloth];
  $<HTMLElement>('swatch-accent').style.background = ACCENTS[look.accent];
  $('head-label').textContent = HEAD_LABELS[look.head];
  $('survivor-label').textContent = SURVIVORS[look.survivor ?? 0].name;
}

function buildOrigins(): void {
  const root = $('origins');
  for (const item of ORIGINS) {
    const b = el('button', 'origin' + (item.id === origin ? ' on' : ''));
    b.type = 'button';
    b.append(el('b', undefined, item.name), el('span', undefined, item.blurb));
    b.addEventListener('click', () => {
      origin = item.id;
      for (const child of root.children) child.classList.remove('on');
      b.classList.add('on');
    });
    root.append(b);
  }
}

function refreshMuteLabel(): void {
  $('menu-mute').textContent = isMuted() ? 'Sound: off' : 'Sound: on';
  $('menu-music').textContent = isMusicOn() ? 'Music: on' : 'Music: off';
}

function cycleLook(key: 'skin' | 'cloth' | 'accent' | 'head' | 'survivor', dir: number): void {
  if (key === 'survivor') look.survivor = ((look.survivor ?? 0) + dir + SURVIVORS.length) % SURVIVORS.length;
  else if (key === 'head') {
    const index = HEADS.indexOf(look.head);
    look.head = HEADS[(index + dir + HEADS.length) % HEADS.length] as Head;
  } else if (key === 'skin') look.skin = (look.skin + dir + SKINS.length) % SKINS.length;
  else if (key === 'cloth') look.cloth = (look.cloth + dir + CLOTHS.length) % CLOTHS.length;
  else look.accent = (look.accent + dir + ACCENTS.length) % ACCENTS.length;
  paintLook();
}

const seenHp = new Map<number, number>();
const BIG = new Set(['surveyor', 'mainspring', 'lastnumber', 'foreman', 'panback', 'slug', 'crab', 'drawer', 'mast', 'count', 'leaker', 'kettle', 'master', 'wanderer', 'walker', 'clerk']);

function noteSmacks(list: ActorSnap[]): void {
  const live = new Set<number>();
  for (const actor of list) {
    if (actor.kind === 'player' || actor.kind === 'dog') continue;
    live.add(actor.id);
    const prev = seenHp.get(actor.id);
    if (prev != null && actor.hp < prev) sfxSmack(BIG.has(actor.kind));
    if (actor.hp > 0) seenHp.set(actor.id, actor.hp);
    else seenHp.delete(actor.id);
  }
  for (const id of seenHp.keys()) if (!live.has(id)) seenHp.delete(id);
}

function sfxSwing(): void {
  sfxWeapon(stats.melee.item?.base ?? null);
}

function sfxShot(): void {
  const gun = stats.ranged?.item?.base;
  if (!gun) return;
  sfxWeapon(gun);
  const art = BASES[gun]?.art;
  if (art === 'needle') shake = Math.max(shake, 3.5);
  if (art === 'can' || art === 'scatter' || art === 'box') shake = Math.max(shake, 2.2);
}

/** A live boss close by, or one that was a moment ago (so the music doesn't flicker at the edge). */
let bossSeenAt = -99;
function bossNear(): boolean {
  if (actors.some((actor) => actor.boss && !actor.dead && actor.hp > 0 && Math.hypot(actor.x - predX, actor.y - predY) < 300)) bossSeenAt = time;
  return time - bossSeenAt < 4;
}

function moodHere(): Mood | null {
  const tx = Math.floor(predX / TILE);
  const ty = Math.floor(predY / TILE);
  const region = regionAtTile(tx, ty);
  if (region.mood) return region.mood as Mood;
  const tile = tiles[ty]?.[tx];
  if (tile === T_GLOW || tile === T_TOXIC) return 'leak';
  return null;
}

// ---------------------------------------------------------------------------
// Wiring.

$('tab-login').addEventListener('click', () => {
  authMode = 'login';
  $('tab-login').classList.add('on');
  $('tab-register').classList.remove('on');
  $('auth-submit').textContent = 'Open the door';
  $<HTMLInputElement>('auth-pass').autocomplete = 'current-password';
});
$('tab-register').addEventListener('click', () => {
  authMode = 'register';
  $('tab-register').classList.add('on');
  $('tab-login').classList.remove('on');
  $('auth-submit').textContent = 'Take the seat';
  $<HTMLInputElement>('auth-pass').autocomplete = 'new-password';
});

$('auth-form').addEventListener('submit', async (ev) => {
  ev.preventDefault();
  const username = $<HTMLInputElement>('auth-user').value.trim();
  const password = $<HTMLInputElement>('auth-pass').value;
  $('auth-error').textContent = '';
  const result = await api(authMode === 'register' ? '/api/register' : '/api/login', { username, password });
  if (!result.ok) {
    $('auth-error').textContent = typeof result.data.error === 'string' ? result.data.error : 'The door stuck.';
    return;
  }
  if (authMode === 'login' && result.data.hasCharacter) connect();
  else setMode('create');
});

$('create-form').addEventListener('submit', async (ev) => {
  ev.preventDefault();
  $('create-error').textContent = '';
  const name = $<HTMLInputElement>('create-name').value.trim();
  const result = await api('/api/character', { name, ...look, origin });
  if (!result.ok) {
    $('create-error').textContent = typeof result.data.error === 'string' ? result.data.error : 'That face did not take.';
    return;
  }
  connect();
});

for (const b of document.querySelectorAll<HTMLButtonElement>('.cycler button')) {
  b.addEventListener('click', () => cycleLook(b.dataset.k as 'skin' | 'cloth' | 'accent' | 'head' | 'survivor', Number(b.dataset.d)));
}

const toggle = (next: Exclude<Panel, null>) => () => (panel === next ? closePanel() : openPanel(next));
$('open-pack').addEventListener('click', toggle('pack'));
$('open-map').addEventListener('click', toggle('map'));
$('open-jobs').addEventListener('click', toggle('jobs'));
$('open-sheet').addEventListener('click', toggle('sheet'));
for (const id of ['pack', 'shop', 'stash', 'board', 'coach', 'jobs', 'sheet', 'map']) $(`${id}-close`).addEventListener('click', closePanel);
$('shop-sell-plain').addEventListener('click', () => send({ t: 'sellPlain' }));
$('map-canvas').addEventListener('click', (ev) => {
  if (!inv) return;
  const c = $<HTMLCanvasElement>('map-canvas');
  const r = c.getBoundingClientRect();
  const mx = ev.clientX - r.left;
  const my = ev.clientY - r.top;
  let best: { id: string; d: number } | null = null;
  for (const mark of MARKS) {
    if (!inv.marks.includes(mark.id) || pastEdge(mark.y)) continue;
    const px = mapFit.ox + (mark.x / TILE) * mapFit.scale;
    const py = mapFit.oy + (mark.y / TILE) * mapFit.scale;
    const d = Math.hypot(px - mx, py - my);
    if (d < 14 && (!best || d < best.d)) best = { id: mark.id, d };
  }
  if (best) pointAtMark(best.id);
});
$('menu-resume').addEventListener('click', () => {
  menuOpen = false;
  hide($('menu'));
});
$('menu-mute').addEventListener('click', () => {
  setMuted(!isMuted());
  localStorage.setItem('radville-mute', isMuted() ? '1' : '0');
  refreshMuteLabel();
});
$('menu-music').addEventListener('click', () => {
  setMusicOn(!isMusicOn());
  localStorage.setItem('radville-music', isMusicOn() ? '1' : '0');
  refreshMuteLabel();
});
$('menu-logout').addEventListener('click', async () => {
  loggingOut = true;
  menuOpen = false;
  await api('/api/logout', {});
  ws?.close();
  ws = null;
  you = null;
  inv = null;
  setMode('auth');
});
$('conn-reload').addEventListener('click', () => location.reload());

$('chat-form').addEventListener('submit', (ev) => {
  ev.preventDefault();
  const input = $<HTMLInputElement>('chat-input');
  const text = input.value.trim();
  if (text) send({ t: 'chat', text });
  input.value = '';
  input.blur();
});
$('chat-input').addEventListener('focus', () => {
  typing = true;
});
$('chat-input').addEventListener('blur', () => {
  typing = false;
});

window.addEventListener('keydown', (ev) => {
  if (ev.code === 'Tab' || ev.code === 'AltLeft' || ev.code === 'AltRight') ev.preventDefault();
  keys.add(ev.code);
  if (mode !== 'play') return;
  const field = ev.target instanceof HTMLInputElement;
  if (ev.code === 'Enter' && !field) {
    ev.preventDefault();
    $<HTMLInputElement>('chat-input').focus();
    return;
  }
  if (ev.code === 'Escape') {
    if (field) {
      (ev.target as HTMLInputElement).blur();
      return;
    }
    if (panel) return closePanel();
    menuOpen = !menuOpen;
    $('menu').classList.toggle('hidden', !menuOpen);
    return;
  }
  if (field || ev.repeat || menuOpen) return;
  if (ev.code === 'KeyI' || ev.code === 'KeyB') return toggle('pack')();
  if (ev.code === 'KeyM') return toggle('map')();
  if (ev.code === 'KeyJ') return toggle('jobs')();
  if (ev.code === 'KeyC') return toggle('sheet')();
  if (panel === 'map') return;
  if (ev.code === 'KeyF') {
    meleeLatch = true;
    sfxSwing();
    sendInput();
  }
  if (ev.code === 'KeyE') {
    interactLatch = true;
    sendInput();
  }
  if (ev.code === 'KeyQ') send({ t: 'cycle' });
  if (ev.code === 'KeyH') stitch();
  if (ev.code === 'KeyR') send({ t: 'barUse', slot: barSlot });
  if (ev.code.startsWith('Digit')) {
    const n = Number(ev.code.slice(5));
    if (n >= 1 && n <= 6) {
      barSlot = n - 1;
      hotKey = '';
      renderHotbar();
      if (panel === 'pack') renderPack();
    }
  }
  if (ev.code === 'Space') {
    ev.preventDefault();
    shootHeld = true;
    shootLatch = true;
    sfxShot();
    sendInput();
  }
});
window.addEventListener(
  'wheel',
  (ev) => {
    if (mode !== 'play' || typing || menuOpen || panel) return;
    barSlot = (barSlot + (ev.deltaY > 0 ? 1 : 5)) % 6;
    hotKey = '';
    renderHotbar();
  },
  { passive: true },
);
window.addEventListener('keyup', (ev) => {
  keys.delete(ev.code);
  if (ev.code === 'Space') shootHeld = false;
});
canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
window.addEventListener('mousemove', (ev) => {
  mouseX = ev.clientX;
  mouseY = ev.clientY;
});
canvas.addEventListener('mousedown', (ev) => {
  if (mode !== 'play' || menuOpen || typing) return;
  if (panel && panel !== 'talk' && panel !== 'fire') {
    // Clicking the world closes whatever you were looking at.
    closePanel();
    return;
  }
  if (ev.button === 0) {
    if (hoverDrop != null) {
      send({ t: 'pick', id: hoverDrop });
      return;
    }
    meleeLatch = true;
    sfxSwing();
    sendInput();
  }
  if (ev.button === 2) {
    shootHeld = true;
    shootLatch = true;
    sfxShot();
    sendInput();
  }
});
window.addEventListener('mouseup', (ev) => {
  if (ev.button === 2) shootHeld = false;
});
window.addEventListener('blur', () => {
  keys.clear();
  shootHeld = false;
});

setInterval(sendInput, 50);
buildOrigins();
paintLook();

// A read-only peek for local play-testing. Only on this machine.
if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
  (window as unknown as Record<string, unknown>).__radville = {
    get you() {
      return you;
    },
    get inv() {
      return inv;
    },
    get stats() {
      return stats;
    },
    get pos() {
      return { x: predX, y: predY, tx: Math.floor(predX / TILE), ty: Math.floor(predY / TILE) };
    },
    get actors() {
      return actors;
    },
    get drops() {
      return worldDrops;
    },
    get panel() {
      return panel;
    },
    send,
  };
}

api('/api/me').then((result) => {
  if (!result.ok) return;
  if (result.data.character) connect();
  else setMode('create');
});

void MAP_H;
requestAnimationFrame(frame);
